import { Box, Card, CircularProgress, Grid, Icon, IconButton, InputLabel, Skeleton, Switch } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSelect from "components/MDSelect";
import MDTypography from "components/MDTypography";
import { useUser } from "context/UserContext";
import { Form, Formik, useFormikContext } from "formik";
import FormField from "layouts/pages/users/new-user/components/FormField";
import PropTypes from "prop-types";
import { memo, useEffect, useState } from 'react';
import { errorAlert, successAlert, formatarTelefone, maskPhoneWithDdd } from "utils/Functions";
import { get, postNoResponse } from "utils/WebService";
import * as Yup from 'yup';
import SubCarteirasList from "./SubCarteirasList";
import ContasBancariasClienteList from "./ContasBancariasClienteList";
import ModeloBoletoCliente from "./ModeloBoletoCliente";

const PessoaCreateEdit = ({ id, closeCreateEdit }) => {

    const {
        nomeCompleto,
        nomeComum,
        identificacao,
        documento,
        tipo,
        estado,
        cidade,
        bairro,
        logradouro,
        numero,
        complemento,
        cep,
        email,
        telefoneResidencial,
        telefoneComercial,
        telefoneCelular,
        tipoPessoa,
        numIdentidade,
        orgaoEmissor,
        instrucaoBoleto1,
        instrucaoBoleto2,
        localPagamento } = configForm.formField

    const [isLoadingInfo, setIsLoadingInfo] = useState(false);
    const [cidadeList, setCidadeList] = useState([]);
    const [estadoList, setEstadoList] = useState([]);
    const [tipoPessoaList, setTipoPessoaList] = useState([]);

    const { tipoPessoaEnum } = useUser();

    useEffect(() => {
        get(`/addresses/states`)
            .then(({ list }) => {
                setEstadoList(list);
            }).catch(() => {
                setEstadoList([]);
            });

        get(`/tipo-pessoa/list-all`)
            .then(({ list }) => {
                setTipoPessoaList(list);
            }).catch(() => {
                setTipoPessoaList([]);
            });
    }, []);

    function selectEstadoCidade(estado) {
        get(`/addresses/cities?stateId=${estado.id}`)
            .then(({ list }) => {
                setCidadeList(list);
            }).catch(() => {
                setCidadeList([]);
            });
    }

    const handleSubmit = (values, actions) => {
        const body = {
            id: values.id,
            nomeCompleto: values.nomeCompleto,
            nomeComum: values.nomeComum,
            identificacao: values.identificacao,
            documento: values.documento ? values.documento.replace(/\D/g, '') : null,
            tipo: values.tipo?.code,
            ativo: values.ativo,
            email: values.email,
            telefoneResidencial: values.telefoneResidencial ? values.telefoneResidencial.replace(/\D/g, '') : null,
            telefoneComercial: values.telefoneComercial ? values.telefoneComercial.replace(/\D/g, '') : null,
            telefone: values.telefoneCelular ? values.telefoneCelular.replace(/\D/g, '') : null,
            bairro: values.bairro,
            logradouro: values.logradouro,
            numero: values.numero,
            complemento: values.complemento,
            cep: values.cep,
            tipoPessoas: values.tipoPessoa,
            municipio: {
                estado: values.estado,
                id: values.cidade?.id,
                descricao: values.cidade?.descricao,
                codMunicipio: values.cidade?.codMunicipio
            },
            instrucaoBoleto1: values.instrucaoBoleto1,
            instrucaoBoleto2: values.instrucaoBoleto2,
            localPagamento: values.localPagamento
        };

        postNoResponse("/pessoa/save", body)
            .then(() => {
                successAlert(!id ? "Cliente criado com sucesso!" : "Alterações salvas!", handleSucess());
            })
            .catch(() => {
                errorAlert(!id ? "Erro ao criar cliente!" : 'Erro ao salvar alterações.');
            })
            .finally(() => {
                actions.setSubmitting(false);
            });
    };

    function handleSucess() {
        closeCreateEdit()
    }

    const handleDocumentoChange = (e, setFieldValue, tipoCode) => {
        let val = e.target.value.replace(/\D/g, '');
        if (tipoCode === 'fisica' || (tipoCode == null && val.length <= 11)) {
            val = val.substring(0, 11);
            val = val.replace(/(\d{3})(\d)/, '$1.$2');
            val = val.replace(/(\d{3})(\d)/, '$1.$2');
            val = val.replace(/(\d{3})(\d{1,2})/, '$1-$2');
        } else {
            val = val.substring(0, 14);
            val = val.replace(/(\d{2})(\d)/, '$1.$2');
            val = val.replace(/(\d{3})(\d)/, '$1.$2');
            val = val.replace(/(\d{3})(\d)/, '$1/$2');
            val = val.replace(/(\d{4})(\d{1,2})/, '$1-$2');
        }
        setFieldValue('documento', val);
    }

    const buscarCep = async (cepStr, setFieldValue) => {
        const cepFormatado = cepStr ? cepStr.replace(/\D/g, '') : '';
        if (cepFormatado.length === 8) {
            try {
                const response = await fetch(`https://viacep.com.br/ws/${cepFormatado}/json/`);
                const data = await response.json();
                if (!data.erro) {
                    setFieldValue('logradouro', data.logradouro);
                    setFieldValue('bairro', data.bairro);
                    if (data.complemento) setFieldValue('complemento', data.complemento);

                    const foundState = estadoList.find(e => e.codEstado === data.uf || e.sigla === data.uf || e.descricao === data.uf || e.nome === data.uf);
                    if (foundState) {
                        setFieldValue('estado', foundState);
                        get(`/addresses/cities?stateId=${foundState.id}`)
                            .then(({ list }) => {
                                setCidadeList(list);
                                const foundCity = list.find(c => c.descricao.toUpperCase() === data.localidade.toUpperCase() || c.codMunicipio == data.ibge);
                                if (foundCity) {
                                    setFieldValue('cidade', foundCity);
                                }
                            });
                    }
                }
            } catch (error) {
                console.error("Erro ao buscar CEP:", error);
            }
        }
    }

    const buscarCnpj = async (cnpjStr, setFieldValue, tipoCode) => {
        const cnpjFormatado = cnpjStr ? cnpjStr.replace(/\D/g, '') : '';
        if (cnpjFormatado.length === 14 && (tipoCode === 'juridica' || tipoCode == null)) {
            try {
                const response = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${cnpjFormatado}`);
                const data = await response.json();
                
                if (data.razao_social) {
                    setFieldValue('nomeCompleto', data.razao_social);
                    setFieldValue('nomeComum', data.nome_fantasia || data.razao_social);
                    
                    const pjType = tipoPessoaEnum?.find(t => t.code === 'juridica');
                    if (pjType) setFieldValue('tipo', pjType);
                    
                    if (data.logradouro) setFieldValue('logradouro', data.logradouro);
                    if (data.numero) setFieldValue('numero', data.numero);
                    if (data.complemento) setFieldValue('complemento', data.complemento);
                    if (data.bairro) setFieldValue('bairro', data.bairro);
                    
                    if (data.cep) {
                        setFieldValue('cep', data.cep);
                        await buscarCep(data.cep, setFieldValue);
                    }
                }
            } catch (error) {
                console.error("Erro ao buscar CNPJ:", error);
            }
        }
    }

    return (
        <Card id="user-info">
            <Box display="flex" alignItems="center" p={3}>
                <IconButton onClick={closeCreateEdit} sx={{ p: 0, paddingRight: 1, paddingTop: 0.75 }}>
                    <MDTypography variant="h5">
                        <Icon>arrow_back</Icon>
                    </MDTypography>
                </IconButton>
                <MDTypography variant="h6">{id ? "Editar" : "Adicionar"} Cliente</MDTypography>
            </Box>
            <Formik
                initialValues={{
                    id: null,
                    nomeCompleto: '',
                    nomeComum: '',
                    identificacao: '',
                    documento: '',
                    tipo: null,
                    ativo: false,
                    estado: null,
                    municipio: null,
                    bairro: '',
                    logradouro: '',
                    numero: '',
                    complemento: '',
                    cep: '',
                    email: '',
                    telefoneResidencial: '',
                    telefoneComercial: '',
                    telefoneCelular: '',
                    tipoPessoa: null,
                    numIdentidade: '',
                    orgaoEmissor: '',
                    instrucaoBoleto1: '',
                    instrucaoBoleto2: '',
                    localPagamento: ''
                }}
                validationSchema={validationSchema}
                onSubmit={(values, actions) => {
                    actions.setSubmitting(true);
                    handleSubmit(values, actions);
                }}>
                {({ values, errors, touched, isSubmitting, setFieldValue }) => {
                    return (
                        <Form id={configForm.formId} autoComplete="off">
                            <FetchAndSetData
                                id={id}
                                setIsLoadingInfo={setIsLoadingInfo}
                                closeCreateEdit={closeCreateEdit} />

                            <MDBox px={3}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={3}>
                                        <FormField
                                            loading={isLoadingInfo}
                                            type={nomeCompleto.type}
                                            label={nomeCompleto.label}
                                            name={nomeCompleto.name}
                                            value={values.nomeCompleto}
                                            placeholder={nomeCompleto.placeholder}
                                            error={errors.nomeCompleto?.length > 0 && touched.nomeCompleto}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={3}>
                                        <FormField
                                            loading={isLoadingInfo}
                                            type={nomeComum.type}
                                            label={nomeComum.label}
                                            name={nomeComum.name}
                                            value={values.nomeComum}
                                            placeholder={nomeComum.placeholder}
                                            error={errors.nomeComum?.length > 0 && touched.nomeComum}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={3}>
                                        <FormField
                                            loading={isLoadingInfo}
                                            type={documento.type}
                                            label={documento.label}
                                            name={documento.name}
                                            value={values.documento}
                                            placeholder={documento.placeholder}
                                            onChange={(e) => {
                                                handleDocumentoChange(e, setFieldValue, values.tipo?.code);
                                            }}
                                            onKeyUp={(e) => {
                                                const val = e.target.value.replace(/\D/g, '');
                                                if (val.length === 14) {
                                                    buscarCnpj(e.target.value, setFieldValue, values.tipo?.code);
                                                }
                                            }}
                                            error={errors.documento?.length > 0 && touched.documento}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={3}>
                                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Tipo</InputLabel>
                                        <MDSelect
                                            isLoading={isLoadingInfo}
                                            getOptionValue={option => option.code}
                                            getOptionLabel={option => option.name}
                                            name={tipo.name}
                                            placeholder={tipo.placeholder}
                                            value={values.tipo}
                                            options={tipoPessoaEnum}
                                            onChange={selectedOptions => {
                                                setFieldValue('tipo', selectedOptions);
                                            }}
                                        />
                                        {errors.tipo && touched.tipo && (
                                            <MDTypography variant="caption" color="error">
                                                {errors.tipo}
                                            </MDTypography>
                                        )}
                                    </Grid>
                                    {values.tipo?.code == 'fisica' &&
                                        <>
                                            <Grid item xs={12} sm={3}>
                                                <FormField
                                                    loading={isLoadingInfo}
                                                    type={numIdentidade.type}
                                                    label={numIdentidade.label}
                                                    name={numIdentidade.name}
                                                    value={values.numIdentidade}
                                                    placeholder={numIdentidade.placeholder}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={3}>
                                                <FormField
                                                    loading={isLoadingInfo}
                                                    type={orgaoEmissor.type}
                                                    label={orgaoEmissor.label}
                                                    name={orgaoEmissor.name}
                                                    value={values.orgaoEmissor}
                                                    placeholder={orgaoEmissor.placeholder}
                                                />
                                            </Grid>
                                        </>}

                                    <Grid item xs={12} sm={3}>
                                        <FormField
                                            loading={isLoadingInfo}
                                            type={email.type}
                                            label={email.label}
                                            name={email.name}
                                            value={values.email}
                                            placeholder={email.placeholder}
                                            error={errors.email?.length > 0 && touched.email}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={3}>
                                        <FormField
                                            loading={isLoadingInfo}
                                            type={telefoneCelular.type}
                                            label={telefoneCelular.label}
                                            name={telefoneCelular.name}
                                            value={values.telefoneCelular || ""}
                                            onChange={(e) => setFieldValue('telefoneCelular', maskPhoneWithDdd(e.target.value))}
                                            placeholder={telefoneCelular.placeholder}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={3}>
                                        <FormField
                                            loading={isLoadingInfo}
                                            type={telefoneComercial.type}
                                            label={telefoneComercial.label}
                                            name={telefoneComercial.name}
                                            value={values.telefoneComercial || ""}
                                            onChange={(e) => setFieldValue('telefoneComercial', maskPhoneWithDdd(e.target.value))}
                                            placeholder={telefoneComercial.placeholder}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={3}>
                                        <FormField
                                            loading={isLoadingInfo}
                                            type={telefoneResidencial.type}
                                            label={telefoneResidencial.label}
                                            name={telefoneResidencial.name}
                                            value={values.telefoneResidencial || ""}
                                            onChange={(e) => setFieldValue('telefoneResidencial', maskPhoneWithDdd(e.target.value))}
                                            placeholder={telefoneResidencial.placeholder}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={3}>
                                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Tipo de Pessoa</InputLabel>
                                        <MDSelect
                                            isLoading={isLoadingInfo}
                                            isMulti
                                            getOptionValue={option => option.id}
                                            getOptionLabel={option => option.descricao}
                                            name={tipoPessoa.name}
                                            placeholder={tipoPessoa.placeholder}
                                            value={values.tipoPessoa}
                                            options={tipoPessoaList}
                                            onChange={selectedOptions => {
                                                setFieldValue('tipoPessoa', selectedOptions);
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={3}>
                                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Estado</InputLabel>
                                        <MDSelect
                                            isLoading={isLoadingInfo}
                                            getOptionValue={option => option.id}
                                            getOptionLabel={option => option.descricao}
                                            name={estado.name}
                                            placeholder={estado.placeholder}
                                            value={values.estado}
                                            options={estadoList}
                                            onChange={selectedOptions => {
                                                setFieldValue('estado', selectedOptions);
                                                selectEstadoCidade(selectedOptions)
                                            }}
                                        />
                                        {errors.estado && touched.estado && (
                                            <MDTypography variant="caption" color="error">
                                                {errors.estado}
                                            </MDTypography>
                                        )}
                                    </Grid>

                                    <Grid item xs={12} sm={3}>
                                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Cidade</InputLabel>
                                        <MDSelect
                                            isLoading={isLoadingInfo}
                                            getOptionValue={option => option.id}
                                            getOptionLabel={option => option.descricao}
                                            name={cidade.name}
                                            placeholder={cidade.placeholder}
                                            value={values.cidade}
                                            options={cidadeList}
                                            onChange={selectedOptions => {
                                                setFieldValue('cidade', selectedOptions);
                                            }}
                                        />
                                        {errors.cidade && touched.cidade && (
                                            <MDTypography variant="caption" color="error">
                                                {errors.cidade}
                                            </MDTypography>
                                        )}
                                    </Grid>

                                    <Grid item xs={12} sm={3}>
                                        <FormField
                                            loading={isLoadingInfo}
                                            type={bairro.type}
                                            label={bairro.label}
                                            name={bairro.name}
                                            value={values.bairro}
                                            placeholder={bairro.placeholder}
                                            error={errors.bairro?.length > 0 && touched.bairro}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={3}>
                                        <FormField
                                            loading={isLoadingInfo}
                                            type={logradouro.type}
                                            label={logradouro.label}
                                            name={logradouro.name}
                                            value={values.logradouro}
                                            placeholder={logradouro.placeholder}
                                            error={errors.logradouro?.length > 0 && touched.logradouro}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={3}>
                                        <FormField
                                            loading={isLoadingInfo}
                                            type={numero.type}
                                            label={numero.label}
                                            name={numero.name}
                                            value={values.numero}
                                            placeholder={numero.placeholder}
                                            error={errors.numero?.length > 0 && touched.numero}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={3}>
                                        <FormField
                                            loading={isLoadingInfo}
                                            type={complemento.type}
                                            label={complemento.label}
                                            name={complemento.name}
                                            value={values.complemento}
                                            placeholder={complemento.placeholder}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={3}>
                                        <FormField
                                            loading={isLoadingInfo}
                                            type={cep.type}
                                            label={cep.label}
                                            name={cep.name}
                                            value={values.cep}
                                            placeholder={cep.placeholder}
                                            onKeyUp={(e) => {
                                                const val = e.target.value.replace(/\D/g, '');
                                                if (val.length === 8) {
                                                    buscarCep(e.target.value, setFieldValue);
                                                }
                                            }}
                                            error={errors.cep?.length > 0 && touched.cep}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={2} display="flex" justifyContent="center" alignItems="center">
                                        {isLoadingInfo ? <Skeleton width={"80%"} sx={{ mt: 2 }} /> :
                                            <Box display="flex" justifyContent="end" alignItems="center" lineHeight={1}>
                                                <MDTypography variant="button">
                                                    Ativo
                                                </MDTypography>
                                                <Box ml={1}>
                                                    <Switch
                                                        checked={values.ativo || false}
                                                        onChange={(event) => setFieldValue('ativo', event.target.checked)} />
                                                </Box>
                                            </Box>
                                        }
                                    </Grid>
                                </Grid>
                            </MDBox>

                            <MDBox px={3}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} mt={3}>
                                        <MDTypography variant="h6" gutterBottom>
                                            Instruções de Boleto
                                        </MDTypography>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <FormField
                                            loading={isLoadingInfo}
                                            type={instrucaoBoleto1.type}
                                            label={instrucaoBoleto1.label}
                                            name={instrucaoBoleto1.name}
                                            value={values.instrucaoBoleto1 ?? ""}
                                            placeholder={instrucaoBoleto1.placeholder}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <FormField
                                            loading={isLoadingInfo}
                                            type={instrucaoBoleto2.type}
                                            label={instrucaoBoleto2.label}
                                            name={instrucaoBoleto2.name}
                                            value={values.instrucaoBoleto2 ?? ""}
                                            placeholder={instrucaoBoleto2.placeholder}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <FormField
                                            loading={isLoadingInfo}
                                            type={localPagamento.type}
                                            label={localPagamento.label}
                                            name={localPagamento.name}
                                            value={values.localPagamento ?? ""}
                                            placeholder={localPagamento.placeholder}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sx={{ pt: "8px !important" }}>
                                        <MDTypography variant="caption" color="text" sx={{ px: 1, display: "block" }}>
                                            Variáveis: {"{acordo}"}, {"{parcela}"}, {"{total_parcelas}"}, {"{vencimento}"}, {"{valor}"}, {"{pagador}"}, {"{contrato}"}, {"{vencimento_contrato}"}.
                                            No modelo Itaú, use | para quebrar a instrução em linhas (até 5 no total). Ex.: NÃO RECEBER APÓS 30 DIAS DO VENCIMENTO | NÚMERO DO ACORDO: {"{acordo}"}
                                        </MDTypography>
                                    </Grid>
                                </Grid>
                            </MDBox>

                            <Box display="flex" alignItems="center" justifyContent="end" p={2}>
                                <MDButton
                                    disabled={isLoadingInfo}
                                    size="large"
                                    variant="contained"
                                    color="info"
                                    circular={false}
                                    iconOnly={false}
                                    type="submit"
                                    endIcon={isSubmitting ?
                                        <CircularProgress
                                            color="inherit"
                                            size={20} sx={{ ml: 0, mr: 0 }} /> : null}>
                                    {!isSubmitting && (id ? "Salvar" : "Adicionar")}
                                </MDButton>
                            </Box>
                        </Form>)
                }}
            </Formik>
            {id && <SubCarteirasList pessoaId={id} />}
            {id && <ContasBancariasClienteList pessoaId={id} />}
            {id && <ModeloBoletoCliente pessoaId={id} />}
        </Card>
    );
};

PessoaCreateEdit.propTypes = {
    id: PropTypes.string,
    closeCreateEdit: PropTypes.func.isRequired,
};

export default memo(PessoaCreateEdit);

const FetchAndSetData = ({ id, setIsLoadingInfo, closeCreateEdit }) => {
    const { setFieldValue } = useFormikContext();
    const { tipoPessoaEnum } = useUser();

    useEffect(() => {
        if (id) {
            setIsLoadingInfo(true);
            get(`/pessoa/by-id/${id}`).then(data => {
                const {
                    id,
                    nomeCompleto,
                    nomeComum,
                    identificacao,
                    documento,
                    tipoPessoas,
                    tipo,
                    ativo,
                    bairro,
                    logradouro,
                    numero,
                    complemento,
                    cep,
                    email,
                    telefone,
                    telefoneComercial,
                    telefoneResidencial,
                    municipio,
                    instrucaoBoleto1,
                    instrucaoBoleto2,
                    localPagamento
                } = data;
                setFieldValue('id', id);
                setFieldValue('nomeCompleto', nomeCompleto);
                setFieldValue('nomeComum', nomeComum);
                setFieldValue('identificacao', identificacao);
                
                let docFormatado = documento ? documento.replace(/\D/g, '') : '';
                if (docFormatado.length > 0) {
                    if (docFormatado.length <= 11) {
                        docFormatado = docFormatado.replace(/(\d{3})(\d)/, '$1.$2')
                                                   .replace(/(\d{3})(\d)/, '$1.$2')
                                                   .replace(/(\d{3})(\d{1,2})/, '$1-$2');
                    } else {
                        docFormatado = docFormatado.replace(/(\d{2})(\d)/, '$1.$2')
                                                   .replace(/(\d{3})(\d)/, '$1.$2')
                                                   .replace(/(\d{3})(\d)/, '$1/$2')
                                                   .replace(/(\d{4})(\d{1,2})/, '$1-$2');
                    }
                }
                setFieldValue('documento', docFormatado);
                
                setFieldValue('tipoPessoa', tipoPessoas);
                setFieldValue('tipo', tipoPessoaEnum?.find(ht => ht.code === tipo));
                setFieldValue('ativo', ativo);
                setFieldValue('bairro', bairro);
                setFieldValue('logradouro', logradouro);
                setFieldValue('numero', numero);
                setFieldValue('complemento', complemento);
                setFieldValue('cep', cep);
                setFieldValue('email', email);
                setFieldValue('telefoneCelular', formatarTelefone(null, telefone));
                setFieldValue('telefoneComercial', formatarTelefone(null, telefoneComercial));
                setFieldValue('telefoneResidencial', formatarTelefone(null, telefoneResidencial));
                setFieldValue('estado', municipio.estado);
                setFieldValue('cidade', municipio);
                setFieldValue('instrucaoBoleto1', instrucaoBoleto1);
                setFieldValue('instrucaoBoleto2', instrucaoBoleto2);
                setFieldValue('localPagamento', localPagamento);
                setIsLoadingInfo(false);
            }).catch((erro) => errorAlert("Cliente não encontrado!", closeCreateEdit));
        }
    }, [id]);

    return null;
};

export const configForm = {
    formId: "configuration-form",
    formField: {
        nomeCompleto: {
            name: "nomeCompleto",
            label: "Razão Social",
            placeholder: "Informe a razão social...",
            errorMsg: "Campo Obrigatório. Somente espaços em brancos não são permitidos.",
        },
        nomeComum: {
            name: "nomeComum",
            label: "Nome Fantasia",
            placeholder: "Informe o nome fantasia...",
            errorMsg: "Campo Obrigatório. Somente espaços em brancos não são permitidos.",
        },
        identificacao: {
            name: "identificacao",
            label: "Identificação",
            placeholder: "Informe a identificação...",
            errorMsg: "Campo Obrigatório. Somente espaços em brancos não são permitidos.",
        },
        documento: {
            name: "documento",
            label: "Documento (CPF/CNPJ)",
            placeholder: "Informe o documento...",
            errorMsg: "Campo Obrigatório. Somente espaços em brancos não são permitidos.",
        },
        tipo: {
            name: "tipo",
            label: "Tipo",
            placeholder: "Selecione",
            errorMsg: "Campo Obrigatório",
        },
        telefoneResidencial: {
            name: "telefoneResidencial",
            label: "Telefone Residencial",
            placeholder: "Informe o telefone residencial...",
        },
        telefoneComercial: {
            name: "telefoneComercial",
            label: "Telefone Comercial",
            placeholder: "Informe o telefone comercial...",
        },
        telefoneCelular: {
            name: "telefoneCelular",
            label: "Telefone Celular",
            placeholder: "Informe o telefone celular...",
        },
        email: {
            name: "email",
            label: "E-mail",
            placeholder: "Informe o e-mail...",
        },
        logradouro: {
            name: "logradouro",
            label: "Logradouro",
            placeholder: "Informe o logradouro...",
            errorMsg: "Campo Obrigatório. Somente espaços em brancos não são permitidos.",

        },
        bairro: {
            name: "bairro",
            label: "Bairro",
            placeholder: "Informe o bairro...",
            errorMsg: "Campo Obrigatório. Somente espaços em brancos não são permitidos.",

        },
        numero: {
            name: "numero",
            label: "Número",
            placeholder: "Informe o número...",
        },
        complemento: {
            name: "complemento",
            label: "Complemento",
            placeholder: "Informe o complemento...",
        },
        cep: {
            name: "cep",
            label: "CEP",
            placeholder: "Informe o CEP...",
            errorMsg: "Campo Obrigatório. Somente espaços em brancos não são permitidos.",

        },
        cidade: {
            name: "cidade",
            label: "Cidade",
            placeholder: "Selecione",
            errorMsg: "Campo Obrigatório.",
        },
        estado: {
            name: "estado",
            label: "Estado",
            placeholder: "Selecione",
            errorMsg: "Campo Obrigatório.",
        },
        tipoPessoa: {
            name: "tipoPessoa",
            label: "Tipo Pessoal",
            placeholder: "Selecione",
        },
        numIdentidade: {
            name: "numIdentidade",
            label: "Nº Identidade",
            placeholder: "Informe o nº da identidade...",
        },
        orgaoEmissor: {
            name: "orgaoEmissor",
            label: "Órgão Emissor",
            placeholder: "Informe o órgão emissor...",
        },
        instrucaoBoleto1: {
            type: 'text',
            name: "instrucaoBoleto1",
            label: "Instrução Boleto 1",
            placeholder: "Ex: Pagável até o vencimento...",
        },
        instrucaoBoleto2: {
            type: 'text',
            name: "instrucaoBoleto2",
            label: "Instrução Boleto 2",
            placeholder: "Ex: Após vencimento cobrar juros...",
        },
        localPagamento: {
            type: 'text',
            name: "localPagamento",
            label: "Local de Pagamento",
            placeholder: "Ex: Pagável em qualquer banco...",
        },
    }
}

const {
    formField: {
        nomeCompleto,
        nomeComum,
        identificacao,
        documento,
        tipo,
        estado,
        cidade
    },
} = configForm;

export const validationSchema = Yup.object().shape({
    [nomeCompleto.name]: Yup.string()
        .required(nomeCompleto.errorMsg),
    [nomeComum.name]: Yup.string()
        .required(nomeComum.errorMsg),
    [documento.name]: Yup.string()
        .required(documento.errorMsg),
    [tipo.name]: Yup.object()
        .required(tipo.errorMsg),
    [estado.name]: Yup.object()
        .required(estado.errorMsg),
    [cidade.name]: Yup.object()
        .required(cidade.errorMsg),
});
